import { PIN_PASS_HEADER } from './pin-pass';
import { ApiHandler } from '../api-handler';
import { ApiEndpoints, HttpMethod } from '../constants';
import { createAuthHeaderValue } from '../utils';
import {
  GetSessionApiResponse,
  CreateSessionUrlApiResponse,
  GenerateTokenPayload,
  CheckSessionApiResponse,
  CheckPatientPinApiResponse,
  CheckPatientPinPayload,
  GetConsultChoicesApiResponse,
  AbandonSessionPayload,
  GetAbandonReasonsApiResponse,
  GetUploadUrlParams,
  GetUploadUrlApiResponse,
  DoctorDashboardAnalyticsApiResponse,
} from './types';
import { GeneralApiResponse } from '../types';
import {
  UpdatePatientInfoPayload,
  UpdatePatientInfoRes,
  TrackAnalyticsApiResponse,
} from './types';
import type { TrackAnalyticsEvent } from '@/lib/models/session';
import type { AnalyticsEventType, AnalyticsInputMode } from '@enluma/common';

/** Allowlisted payloads for the typed analytics events (issue #216). */
export type AnalyticsEventPayload =
  | { mode: AnalyticsInputMode }
  | { from: AnalyticsInputMode; to: AnalyticsInputMode };

/**
 * All session-token-authenticated calls pass the capability JWT in the
 * Authorization header (Bearer scheme) rather than in the URL. Tokens in
 * URLs leak through server access logs, browser history, and Referer headers.
 *
 * See docs/auth-flows.md — "Anonymous Patient Session Flow".
 */
const sessionBearer = (token: string) => createAuthHeaderValue(token);

export const SessionApiService = {
  getSession: (sessionToken: string) =>
    ApiHandler.makeApiRequest<GetSessionApiResponse, undefined>({
      endpoint: ApiEndpoints.Sessions.Current,
      method: HttpMethod.Get,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
    }),
  updatePatientInfo: async (
    username: string,
    patientInfo: UpdatePatientInfoPayload,
  ) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<UpdatePatientInfoRes>,
      UpdatePatientInfoPayload
    >({
      endpoint: ApiEndpoints.Sessions.Patient(username),
      method: HttpMethod.Post,
      withAuth: false,
      data: patientInfo,
      rejectOnError: true,
    }),
  // Public Patient PIN check; rejects on a wrong PIN so callers can show the
  // generic message.
  checkPatientPin: (username: string, payload: CheckPatientPinPayload) =>
    ApiHandler.makeApiRequest<
      CheckPatientPinApiResponse,
      CheckPatientPinPayload
    >({
      endpoint: ApiEndpoints.Sessions.Pin(username),
      method: HttpMethod.Post,
      withAuth: false,
      data: payload,
      rejectOnError: true,
    }),
  // Public consult choices for the patient screen; a PIN Pass (when the
  // doctor's PIN is on) travels in the PIN Pass header.
  getConsultChoices: (username: string, pinPass?: string) =>
    ApiHandler.makeApiRequest<GetConsultChoicesApiResponse>({
      endpoint: ApiEndpoints.Sessions.Consults(username),
      method: HttpMethod.Get,
      withAuth: false,
      ...(pinPass ? { headers: { [PIN_PASS_HEADER]: pinPass } } : {}),
      rejectOnError: true,
    }),
  trackAnalytics: (rejectOnError?: boolean) =>
    ApiHandler.makeApiRequest<TrackAnalyticsApiResponse>({
      endpoint: ApiEndpoints.Sessions.Analytics,
      method: HttpMethod.Get,
      rejectOnError,
    }),
  getDoctorDashboardAnalytics: (window: 7 | 30 = 7) =>
    ApiHandler.makeApiRequest<DoctorDashboardAnalyticsApiResponse>({
      endpoint: `${ApiEndpoints.Sessions.AnalyticsDashboard}?window=${window}`,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),
  trackAnalyticsSession: (sessionToken: string, type: TrackAnalyticsEvent) =>
    ApiHandler.makeApiRequest<void, { type: TrackAnalyticsEvent }>({
      endpoint: ApiEndpoints.Sessions.Analytics,
      method: HttpMethod.Post,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
      data: { type },
    }),
  /**
   * Typed event-log analytics (issue #216). Same endpoint as the legacy
   * counter events; the API branches on `eventType` vs `type`. Once-per-
   * session events are deduplicated server-side, so retries are safe.
   */
  trackAnalyticsEvent: (
    sessionToken: string,
    eventType: AnalyticsEventType,
    payload?: AnalyticsEventPayload,
  ) =>
    ApiHandler.makeApiRequest<
      void,
      { eventType: AnalyticsEventType; payload?: AnalyticsEventPayload }
    >({
      endpoint: ApiEndpoints.Sessions.Analytics,
      method: HttpMethod.Post,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
      data: { eventType, payload },
    }),
  generateToken: (sessionToken: string, body: GenerateTokenPayload) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<{ identity: string; accessToken: string }>,
      GenerateTokenPayload
    >({
      endpoint: ApiEndpoints.Sessions.Token,
      method: HttpMethod.Post,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
      data: body,
    }),
  abandonSession: (sessionToken: string, payload: AbandonSessionPayload) =>
    ApiHandler.makeApiRequest<void, AbandonSessionPayload>({
      endpoint: ApiEndpoints.Sessions.Abandon,
      method: HttpMethod.Post,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
      data: payload,
    }),
  getAbandonReasons: (sessionToken: string) =>
    ApiHandler.makeApiRequest<GetAbandonReasonsApiResponse>({
      endpoint: ApiEndpoints.Sessions.AbandonReasons,
      method: HttpMethod.Get,
      rejectOnError: true,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
    }),
  createSessionUrl: () =>
    ApiHandler.makeApiRequest<CreateSessionUrlApiResponse>({
      endpoint: ApiEndpoints.Sessions.Base,
      method: HttpMethod.Post,
      withAuth: true,
    }),
  /**
   * offeringPath (ADR 0001) pre-validates the link's Offering so a bad
   * offering shows the invalid screen at page load. trackLanding (issue
   * #216, Option A) appends ?src=patient-link so the API records a
   * link_landed metric — rides this existing public status call, no new
   * endpoint. Send it only from the patient landing page, deduped per tab
   * via markLandingTracked(). Both query params are independent and may be
   * present together on the same call.
   */
  checkSession: (
    username: string,
    offeringPath?: string,
    trackLanding = false,
  ) => {
    const params = new URLSearchParams();
    if (offeringPath) params.set('offering', offeringPath);
    if (trackLanding) params.set('src', 'patient-link');
    const query = params.toString();
    return ApiHandler.makeApiRequest<CheckSessionApiResponse>({
      endpoint:
        ApiEndpoints.Sessions.Status(username) + (query ? `?${query}` : ''),
      method: HttpMethod.Get,
      withAuth: false,
    });
  },
  getUploadUrl: ({ sessionToken, filename, contentType }: GetUploadUrlParams) =>
    ApiHandler.makeApiRequest<
      GetUploadUrlApiResponse,
      { filename?: string; contentType: string }
    >({
      endpoint: ApiEndpoints.Sessions.AudioUploadUrl,
      method: HttpMethod.Post,
      withAuth: true,
      accessToken: sessionBearer(sessionToken),
      data: {
        filename,
        contentType,
      },
    }),
} as const;
